import React, { useRef, useEffect, useState } from 'react';

interface PanoViewerProps {
  imageUrl: string;
  canvasWidth?: number;
  canvasHeight?: number;
  onPositionChange?: (pos: number) => void;
}

const PanoViewer: React.FC<PanoViewerProps> = ({
  imageUrl,
  canvasWidth = 800,
  canvasHeight = 400,
  onPositionChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [position, setPosition] = useState(0);
  const [centerYOffset, setCenterYOffset] = useState(0);

  const drawImage = (offset: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const img = imgRef.current;
    if (!canvas || !ctx || !img) return;

    const repeatWidth = img.width;
    const modOffset = offset % repeatWidth;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, -modOffset, centerYOffset);

    // Draw second copy to fill right edge if needed
    if (modOffset > 0) {
      ctx.drawImage(img, repeatWidth - modOffset, centerYOffset);
    }
  };

  // Load image and center it on load
  useEffect(() => {
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      imgRef.current = img;

      const centerX = (img.width - canvasWidth) / 2;
      const centerY = (canvasHeight - img.height) / 2;

      setCenterYOffset(centerY);
      setPosition(centerX);
      drawImage(centerX);
    };
  }, [imageUrl, canvasWidth, canvasHeight]);

  // Redraw when position changes
  useEffect(() => {
    drawImage(position);
    onPositionChange?.(position);
  }, [position]);

  // Drag support
  useEffect(() => {
    let isDragging = false;
    let startX = 0;

    const handleMouseDown = (e: MouseEvent) => {
      isDragging = true;
      startX = e.clientX;
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const delta = e.clientX - startX;
      startX = e.clientX;
      setPosition(prev => prev - delta);
    };

    const handleMouseUp = () => {
      isDragging = false;
    };

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      canvas?.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Update canvas dimensions on props change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
    }
  }, [canvasWidth, canvasHeight]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        opacity: 0.7,
        width: `${canvasWidth}px`,
        height: `${canvasHeight}px`,
        display: 'block',
        cursor: 'grab',
      }}
    />
  );
};

export default PanoViewer;
